import { createBrowserHistory } from "history";
import {
    ActionsTemplatesApi,
    AntennasAApi,
    ArtifactsApi,
    BadgeZoneViolationEventsApi,
    ColumnVisibilitysApi,
    DashboardApi,
    DepartmentsApi,
    EloshieldClassicStationaryModulesApi,
    EloshieldEventsApi,
    EmailLoginTokensApi,
    EventsApi,
    FilesApi,
    FleetAuthenticateEventsApi,
    LoginTokensApi,
    MotionSensorsApi,
    NfcTokensApi,
    PedestrianDevicesApi,
    PersistentNotificationsApi,
    PhonesApi,
    PreOpsAnswersApi,
    PreOpsChecklistsApi,
    PreOpsQuestionVersionsApi,
    PreOpsQuestionsApi,
    PublicSettingsApi,
    SafetyMessagesApi,
    SettingsApi,
    ShiftsApi,
    ShockEventsApi,
    ShockProfileVersionsApi,
    ShockProfilesApi,
    SitesApi,
    StationaryUnitsApi,
    UserGroupAssignmentsApi,
    UserGroupVehicleGroupAssignmentsApi,
    UserGroupsApi,
    UsersApi,
    VehicleDevicesApi,
    VehicleGroupAssignmentsApi,
    VehicleGroupsApi,
    VehicleMovementsApi,
    VehicleTypesApi,
    VehicleZoneViolationEventsApi,
    VehiclesApi,
} from "./api";
import { HistoryProvider } from "./domain/providers/history-provider";
import { I18nProvider } from "./domain/providers/i18n-provider";
import { RepositoryColumnVisibility } from "./domain/repositories/repository-column-visibility";
import { RepositoryDepartments } from "./domain/repositories/repository-departments";
import { RepositoryFleetAuthenticatePeriods } from "./domain/repositories/repository-fleet-authenticate-periods";
import { RepositoryMotionSensors } from "./domain/repositories/repository-motion-sensors";
import { RepositoryNfcTokens } from "./domain/repositories/repository-nfc-tokens";
import { RepositoryPersistentNotifications } from "./domain/repositories/repository-persistent-notifications";
import { RepositoryPhones } from "./domain/repositories/repository-phones";
import { RepositoryPreOpsAnswers } from "./domain/repositories/repository-pre-ops-answers";
import { RepositoryPreOpsChecklists } from "./domain/repositories/repository-pre-ops-checklists";
import { RepositoryPreOpsQuestionVersions } from "./domain/repositories/repository-pre-ops-question-versions";
import { RepositoryPreOpsQuestions } from "./domain/repositories/repository-pre-ops-questions";
import { RepositoryPublicSettings } from "./domain/repositories/repository-public-settings";
import { RepositorySafetyMessages } from "./domain/repositories/repository-safety-messages";
import { RepositorySettings } from "./domain/repositories/repository-settings";
import { RepositoryShifts } from "./domain/repositories/repository-shifts";
import { RepositoryShockEvents } from "./domain/repositories/repository-shock-events";
import { RepositoryShockProfileVersions } from "./domain/repositories/repository-shock-profile-versions";
import { RepositoryShockProfiles } from "./domain/repositories/repository-shock-profiles";
import { RepositorySites } from "./domain/repositories/repository-sites";
import { RepositoryUserGroupAssignments } from "./domain/repositories/repository-user-group-assignments";
import { RepositoryUserGroupVehicleGroupAssignments } from "./domain/repositories/repository-user-group-vehicle-group-assignments";
import { RepositoryUserGroups } from "./domain/repositories/repository-user-groups";
import { RepositoryUsers } from "./domain/repositories/repository-users";
import { RepositoryVehicleDevices } from "./domain/repositories/repository-vehicle-devices";
import { RepositoryVehicleGroupAssignments } from "./domain/repositories/repository-vehicle-group-assignments";
import { RepositoryVehicleGroups } from "./domain/repositories/repository-vehicle-groups";
import { RepositoryVehicleMovementPeriods } from "./domain/repositories/repository-vehicle-movement-periods";
import { RepositoryVehicleTypes } from "./domain/repositories/repository-vehicle-types";
import { RepositoryVehicles } from "./domain/repositories/repository-vehicles";
import { ServiceAuth } from "./domain/services/service-auth";
import { ServiceErrors } from "./domain/services/service-errors";
import { ServiceFile } from "./domain/services/service-file";
import { ServiceHelp } from "./domain/services/service-help";
import { ServiceListStates } from "./domain/services/service-list-states";
import { ServiceLoginToken } from "./domain/services/service-login-token";
import { ServiceOwnUser } from "./domain/services/service-own-user";
import { ServicePublicSettings } from "./domain/services/service-public-settings";
import { ServiceSidebar } from "./domain/services/service-sidebar";
import { ServiceSse } from "./domain/services/service-sse";
import { ServiceStubData } from "./domain/services/service-stub-data";
import { apiConfiguration } from "./utils/api-configuration";
import { RepositoryActionsTemplates } from "./domain/repositories/repository-action-templates";
import { RepositoryPedestrianDevices } from "./domain/repositories/repository-pedestrian-devices";
import { RepositoryEloshieldClassicStationaryModules } from "./domain/repositories/repository-eloshield-classic-stationary-modules";
import { ServiceAgv } from "./domain/services/service-agv";
import { RepositoryStationaryUnits } from "./domain/repositories/repository-stationary-units";
import { ServiceRobots } from "./domain/services/service-robots";

/**
 * This class is responsible for **all** service state handling and dependency management in this app.
 * We don't do any dependency injection any longer, but rather do an early initialization of all necessary services
 * for this app and pass dependencies explicitly down the line in the order they're needed.
 *
 * This design implicitly enforces dependency-cycle free service design.
 *
 * See https://mobx.js.org/defining-data-stores.html#combining-multiple-stores for official documentation on this pattern.
 */
export class RootStore {
    i18n = new I18nProvider();
    historyProvider = new HistoryProvider(createBrowserHistory());
    actionsTemplatesApi = new ActionsTemplatesApi();
    antennasAApi = new AntennasAApi(apiConfiguration);
    artifactsApi = new ArtifactsApi(apiConfiguration);
    badgeZoneViolationEventsApi = new BadgeZoneViolationEventsApi(apiConfiguration);
    eloshieldEventsApi = new EloshieldEventsApi(apiConfiguration);
    eventsApi = new EventsApi(apiConfiguration);
    loginTokensApi = new LoginTokensApi(apiConfiguration);
    motionSensorsApi = new MotionSensorsApi(apiConfiguration);
    vehicleDevicesApi = new VehicleDevicesApi(apiConfiguration);
    vehiclesApi = new VehiclesApi(apiConfiguration);
    vehicleZoneViolationEventsApi = new VehicleZoneViolationEventsApi(apiConfiguration);
    shockEventsApi = new ShockEventsApi(apiConfiguration);
    shockProfilesApi = new ShockProfilesApi(apiConfiguration);
    shockProfileVersionsApi = new ShockProfileVersionsApi(apiConfiguration);
    sitesApi = new SitesApi(apiConfiguration);
    stationaryUnitsApi = new StationaryUnitsApi(apiConfiguration);
    vehicleMovementsApi = new VehicleMovementsApi(apiConfiguration);
    vehicleTypesApi = new VehicleTypesApi(apiConfiguration);
    usersApi = new UsersApi(apiConfiguration);
    usersGroupsApi = new UserGroupsApi(apiConfiguration);
    vehicleGroupsApi = new VehicleGroupsApi(apiConfiguration);
    vehicleGroupAssignmentsApi = new VehicleGroupAssignmentsApi(apiConfiguration);
    userGroupAssignmentsApi = new UserGroupAssignmentsApi(apiConfiguration);
    userGroupVehicleGroupAssignmentsApi = new UserGroupVehicleGroupAssignmentsApi(apiConfiguration);
    nfcTokensApi = new NfcTokensApi(apiConfiguration);
    filesApi = new FilesApi(apiConfiguration);
    dashboardApi = new DashboardApi(apiConfiguration);
    departmentsApi = new DepartmentsApi(apiConfiguration);
    safetyMessageApi = new SafetyMessagesApi(apiConfiguration);
    shiftsApi = new ShiftsApi(apiConfiguration);
    pedestrianDevicesApi = new PedestrianDevicesApi(apiConfiguration);
    eloshieldClassicStationaryModulesApi = new EloshieldClassicStationaryModulesApi(
        apiConfiguration,
    );
    persistentNotificationsApi = new PersistentNotificationsApi(apiConfiguration);
    phonesApi = new PhonesApi(apiConfiguration);
    preOpsChecklistsApi = new PreOpsChecklistsApi(apiConfiguration);
    preOpsQuestionsApi = new PreOpsQuestionsApi(apiConfiguration);
    preOpsQuestionVersionsApi = new PreOpsQuestionVersionsApi(apiConfiguration);
    preOpsAnswersApi = new PreOpsAnswersApi(apiConfiguration);
    settingsApi = new SettingsApi(apiConfiguration);
    publicSettingsApi = new PublicSettingsApi(apiConfiguration);
    columnVisibilitysApi = new ColumnVisibilitysApi(apiConfiguration);
    fleetAuthenticateEventsApi = new FleetAuthenticateEventsApi(apiConfiguration);
    emailLoginTokensApi = new EmailLoginTokensApi(apiConfiguration);

    serviceLoginToken = new ServiceLoginToken();
    serviceStubData = new ServiceStubData(this.i18n);
    serviceErrors = new ServiceErrors();
    serviceAuth = new ServiceAuth(
        this.serviceErrors,
        this.serviceLoginToken,
        this.loginTokensApi,
        this.historyProvider,
    );
    serviceFile = new ServiceFile(this.serviceErrors, this.serviceAuth, this.filesApi, this.i18n);
    serviceSse = new ServiceSse(this.serviceAuth);
    serviceHelp = new ServiceHelp(this.i18n);

    repositoryActionTemplates = new RepositoryActionsTemplates(this.actionsTemplatesApi);
    repositoryColumnVisibility = new RepositoryColumnVisibility(this.columnVisibilitysApi);
    repositoryPublicSettings = new RepositoryPublicSettings(
        this.publicSettingsApi,
        this.serviceFile,
    );
    repositoryNfcTokens = new RepositoryNfcTokens(this.nfcTokensApi);
    repositorySafetyMessages = new RepositorySafetyMessages(this.safetyMessageApi);
    repositoryPersistentNotifications = new RepositoryPersistentNotifications(
        this.persistentNotificationsApi,
    );
    repositoryPedestrianDevices = new RepositoryPedestrianDevices(this.pedestrianDevicesApi);
    repositoryStationaryUnits = new RepositoryStationaryUnits(this.stationaryUnitsApi);
    repositoryEloshieldClassicStationaryModules = new RepositoryEloshieldClassicStationaryModules(
        this.eloshieldClassicStationaryModulesApi,
    );
    repositoryPhones = new RepositoryPhones(this.phonesApi);
    repositoryPreOpsChecklists = new RepositoryPreOpsChecklists(this.preOpsChecklistsApi);
    repositoryPreOpsQuestions = new RepositoryPreOpsQuestions(this.preOpsQuestionsApi);
    repositoryPreOpsQuestionVersions = new RepositoryPreOpsQuestionVersions(
        this.preOpsQuestionVersionsApi,
    );
    repositoryPreOpsAnswers = new RepositoryPreOpsAnswers(this.preOpsAnswersApi);
    repositoryShockEvents = new RepositoryShockEvents(this.shockEventsApi);
    repositoryShockProfiles = new RepositoryShockProfiles(this.shockProfilesApi);
    repositoryShockProfileVersions = new RepositoryShockProfileVersions(
        this.shockProfileVersionsApi,
    );
    repositorySites = new RepositorySites(this.sitesApi);
    repositoryShifts = new RepositoryShifts(this.shiftsApi);

    repositoryUserGroupAssignments = new RepositoryUserGroupAssignments(
        this.userGroupAssignmentsApi,
    );
    repositoryVehicleDevices = new RepositoryVehicleDevices(this.vehicleDevicesApi);
    repositoryVehicleTypes = new RepositoryVehicleTypes(this.vehicleTypesApi);
    repositoryVehicleGroupAssignments = new RepositoryVehicleGroupAssignments(
        this.vehicleGroupAssignmentsApi,
    );
    repositoryVehicleMovementPeriods = new RepositoryVehicleMovementPeriods(
        this.vehicleMovementsApi,
    );
    repositoryFleetAuthenticatePeriods = new RepositoryFleetAuthenticatePeriods(
        this.fleetAuthenticateEventsApi,
    );
    repositoryVehicles = new RepositoryVehicles(this.vehiclesApi);
    repositoryMotionSensors = new RepositoryMotionSensors(this.motionSensorsApi);

    repositoryDepartments = new RepositoryDepartments(this.departmentsApi);
    repositoryUsers = new RepositoryUsers(this.usersApi, this.filesApi, this.serviceFile);

    serviceOwnUser = new ServiceOwnUser(
        this.serviceAuth,
        this.repositoryUsers,
        this.repositoryDepartments,
    );
    servicePublicSettings = new ServicePublicSettings(this.serviceOwnUser);
    serviceSidebar = new ServiceSidebar(this.serviceFile, this.servicePublicSettings);

    repositoryUserGroups = new RepositoryUserGroups(this.usersGroupsApi, this.serviceOwnUser);
    repositorySettings = new RepositorySettings(this.settingsApi);
    repositoryVehicleGroups = new RepositoryVehicleGroups(
        this.vehicleGroupsApi,
        this.serviceOwnUser,
    );
    repositoryUserGroupVehicleGroupAssignments = new RepositoryUserGroupVehicleGroupAssignments(
        this.userGroupVehicleGroupAssignmentsApi,
        this.repositoryUserGroups,
        this.repositoryVehicleGroups,
    );
    serviceListStates = new ServiceListStates(
        this.historyProvider,
        this.repositoryDepartments,
        this.repositoryMotionSensors,
        this.repositoryPreOpsChecklists,
        this.repositoryPreOpsQuestions,
        this.repositoryShifts,
        this.repositoryShockEvents,
        this.repositoryShockProfiles,
        this.repositorySafetyMessages,
        this.repositorySites,
        this.repositoryUserGroupAssignments,
        this.repositoryUserGroups,
        this.repositoryUsers,
        this.repositoryVehicleDevices,
        this.repositoryVehicleGroupAssignments,
        this.repositoryVehicleGroups,
        this.repositoryVehicleMovementPeriods,
        this.repositoryVehicleTypes,
        this.repositoryVehicles,
        this.repositoryPedestrianDevices,
        this.repositoryPhones,
        this.repositoryPreOpsAnswers,
        this.repositoryFleetAuthenticatePeriods,
        this.repositoryStationaryUnits,
        this.repositoryEloshieldClassicStationaryModules,
    );
    serviceAgv = new ServiceAgv();
    serviceRobots = new ServiceRobots();

    async initialize(): Promise<void> {
        await this.i18n.initialize();
    }
}

export let store: RootStore;

export function setGlobalStore(val: RootStore): void {
    store = val;
}
